import { Box, Button, Divider, InputLabel, Paper, Stack, TextField, Typography } from '@mui/material'
import { type FormEvent, useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { GoogleSignInButton } from 'src/components/atoms/GoogleSignInButton'
import { noreplyEmail, serviceName } from 'src/config'
import { useAuth } from 'src/hooks/useAuth'

export const LoginPage = () => {
  const [email, setEmail] = useState<string>('')
  const { sendAuthEmail, googleLogin } = useAuth()

  const handleClick = useCallback(
    async (e: FormEvent) => {
      try {
        e.preventDefault()
        if (!email) {
          toast.error('メールアドレスを入力してください')
          return
        }
        await sendAuthEmail(email)
        toast.success('メールを送信しました')
        setEmail('')
      } catch {
        toast.error('メールの送信に失敗しました')
        setEmail('')
      }
    },
    [email, sendAuthEmail],
  )

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 4, md: 8 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 480,
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <img src="/logomark-color.svg" alt="logo" height="44" />
          <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: 22 }}>{serviceName}</Typography>
          <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, mt: 1 }}>
            ログイン
          </Typography>
        </Stack>

        <Stack
          component="form"
          spacing={2.5}
          onSubmit={handleClick}
          sx={{
            mt: 4,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              ログインリンクで認証
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.7, display: 'block' }}>
              ご入力のメールアドレスにログインリンクを送信します。リンクをクリックするだけでログインできます。
              {noreplyEmail && (
                <>
                  <br />
                  送信元: {noreplyEmail}
                </>
              )}
            </Typography>
          </Box>
          <Box>
            <InputLabel sx={{ mb: 0.5, fontWeight: 600 }}>メールアドレス</InputLabel>
            <TextField
              fullWidth
              size="medium"
              name="email"
              type="email"
              required
              placeholder="example@example.com"
              onChange={(e) => {
                e.preventDefault()
                setEmail(e.currentTarget.value)
              }}
              value={email}
            />
          </Box>
          <Button type="submit" variant="contained" size="large" sx={{ borderRadius: 999, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 2 } }}>
            リンクを送信
          </Button>
        </Stack>

        <Box sx={{ position: 'relative', my: 4 }}>
          <Divider />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'background.paper',
              px: 1.5,
              color: 'text.secondary',
            }}
          >
            または
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <GoogleSignInButton
            onClick={async () => {
              await googleLogin()
            }}
          />
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 4, textAlign: 'center', color: 'text.secondary' }}>
          アカウントをお持ちでない方は
          <Link to="/inquiry/register" style={{ color: 'inherit', textDecoration: 'underline', marginLeft: 4 }}>
            ご利用申請
          </Link>
        </Typography>
      </Paper>
    </Box>
  )
}
